import {
  onSnapshot, collection, orderBy, query, where, Unsubscribe,
} from 'firebase/firestore';
import { db } from '../FirebaseConfig';
import Client from '../models/ClientType';
import Suivi from '../models/Suivi';
import Visite from '../models/Visite';
import VisibilityState from '../models/enums/VisibilityState';
import UserService from './UserService';

class QueryService {
  private async getIdsToWatch(userId: string, showAll: boolean): Promise<string[]> {
    if (!showAll) {
      return [userId];
    }
    try {
      const sellers = await UserService.getAllSellers(userId);
      const sellerIds = sellers.map((s) => s.id);
      return [userId, ...sellerIds];
    } catch (error) {
      console.error(`Failed to fetch sellers for user ${userId}. Falling back to parent only.`, error);
      return [userId];
    }
  }

  watchClients(userId: string, showAll: boolean, callback: (clients: Client[]) => void): Unsubscribe {
    const unsubscribes: Unsubscribe[] = [];
    const allData: { [id: string]: Client[] } = {};

    this.getIdsToWatch(userId, showAll).then((idsToWatch) => {
      idsToWatch.forEach((id) => {
        const q = query(
          collection(db, 'Users', id, 'Clients'),
          where('visibility', '==', VisibilityState.VISIBLE),
          orderBy('name'),
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const items: Client[] = [];
          snapshot.forEach((doc) => {
            items.push({ ...doc.data(), id: doc.id } as Client);
          });
          allData[id] = items;
          const mergedData = Object.values(allData).flat().sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
          callback(mergedData);
        });
        unsubscribes.push(unsubscribe);
      });
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }

  watchSuivis(userId: string, showAll: boolean, callback: (suivis: Suivi[]) => void): Unsubscribe {
    const unsubscribes: Unsubscribe[] = [];
    const allData: { [id: string]: Suivi[] } = {};

    this.getIdsToWatch(userId, showAll).then((idsToWatch) => {
      idsToWatch.forEach((id) => {
        const q = query(
          collection(db, 'Users', id, 'Suivis'),
          where('visibility', '==', VisibilityState.VISIBLE),
          orderBy('date', 'desc'),
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const items: Suivi[] = [];
          snapshot.forEach((doc) => {
            items.push({ ...doc.data(), id: doc.id } as Suivi);
          });
          allData[id] = items;
          const mergedData = Object.values(allData).flat().sort((a, b) => b.date.toMillis() - a.date.toMillis());
          callback(mergedData);
        });
        unsubscribes.push(unsubscribe);
      });
    });
    return () => unsubscribes.forEach((unsub) => unsub());
  }

  watchVisites(userId: string, showAll: boolean, callback: (visites: Visite[]) => void): Unsubscribe {
    const unsubscribes: Unsubscribe[] = [];
    const allData: { [id: string]: Visite[] } = {};

    this.getIdsToWatch(userId, showAll).then((idsToWatch) => {
      idsToWatch.forEach((id) => {
        const q = query(
          collection(db, 'Users', id, 'Visites'),
          orderBy('date', 'desc'),
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const items: Visite[] = [];
          snapshot.forEach((doc) => {
            items.push({ ...doc.data(), id: doc.id } as Visite);
          });
          allData[id] = items;
          const mergedData = Object.values(allData).flat().sort((a, b) => b.date.toMillis() - a.date.toMillis());
          callback(mergedData);
        });
        unsubscribes.push(unsubscribe);
      });
    });
    return () => unsubscribes.forEach((unsub) => unsub());
  }
}

export default new QueryService();
